import { Chip, ClickAwayListener, Paper, TextField } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { stateText } from "../../styles/aerosTokens";

// Mesmo padrão de parsing usado em aerosTokens.js (mixHex/contrastTextFor):
// aplica opacidade a uma cor hex sem depender de um helper exportado do tema.
const withAlpha = (hex, alpha) => {
    if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
        return `rgba(160, 68, 255, ${alpha})`;
    }
    const s = hex.replace("#", "").slice(0, 6);
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const useStyles = makeStyles((theme) => ({
    // Barra slim de tags do topo da conversa (mockup Aeros Dark):
    // 40px, label "TAGS" + pills; a edição (Autocomplete) só aparece sob demanda.
    tagsBar: {
        flex: "none",
        minHeight: 40,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "0 20px",
        background: theme.palette.aeros.surface,
        borderBottom: `1px solid ${theme.mode === "light" ? theme.palette.divider : "rgba(255, 255, 255, 0.06)"}`,
    },
    tagsLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: theme.palette.aeros.textTertiary,
        flex: "none",
    },
    tagPill: {
        fontSize: 10.5,
        fontWeight: 600,
        borderRadius: 99,
        padding: "2px 10px",
        whiteSpace: "nowrap",
    },
    addTagButton: {
        width: 20,
        height: 20,
        flex: "none",
        boxSizing: "border-box",
        borderRadius: 99,
        border: `1px dashed ${theme.mode === "light" ? "rgba(15, 20, 40, 0.25)" : "rgba(255, 255, 255, 0.2)"}`,
        background: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.aeros.textTertiary,
        fontSize: 13,
        lineHeight: 1,
        cursor: "pointer",
        padding: 0,
    },
    editWrapper: {
        flex: 1,
        minWidth: 0,
        padding: "4px 0",
    },
}));

export function TagsContainer({ contact }) {
    const theme = useTheme();
    const classes = useStyles();

    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const [editing, setEditing] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            loadTags().then(() => {
                if (Array.isArray(contact.tags)) {
                    setSelecteds(contact.tags);
                } else {
                    setSelecteds([]);
                }
            });
        }
    }, [contact]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`, 
            {params: { kanban: 0}
        });
            setTags(data);
        } catch (err) {
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (item.length < 3) {
                        toastError("Tag muito curta!");
                        return;
                    }
                    if (isString(item)) {
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        setSelecteds(optionsChanged);
        await syncTags({ contactId: contact.id, tags: optionsChanged });
    }

    function getRandomHexColor() {
        // Gerar valores aleatórios para os componentes de cor
        const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255
      
        // Converter os componentes de cor em uma cor hexadecimal
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
      
        return hexColor;
    }

    return (
        <div className={classes.tagsBar}>
            <span className={classes.tagsLabel}>Tags</span>
            {!editing && (
                <>
                    {selecteds.map((option) => (
                        <span
                            key={option.id || option.name}
                            className={classes.tagPill}
                            style={{
                                backgroundColor: withAlpha(option.color || theme.palette.aeros.textTertiary, 0.1),
                                color: option.color ? stateText(theme, option.color) : theme.palette.text.primary,
                            }}
                        >
                            {option.name}
                        </span>
                    ))}
                    <button
                        type="button"
                        className={classes.addTagButton}
                        aria-label="Editar tags"
                        title="Editar tags"
                        onClick={() => setEditing(true)}
                    >
                        +
                    </button>
                </>
            )}
            {editing && (
                <ClickAwayListener onClickAway={() => setEditing(false)}>
                    <div className={classes.editWrapper}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={tags}
                            value={selecteds}
                            freeSolo
                            openOnFocus
                            onChange={(e, v, r) => onChange(v, r)}
                            getOptionLabel={(option) => option.name}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        style={{
                                            backgroundColor: withAlpha(option.color || theme.palette.aeros.textTertiary, 0.1),
                                            border: "none",
                                            color: option.color ? stateText(theme, option.color) : theme.palette.text.primary,
                                            marginRight: 4,
                                            fontWeight: 600,
                                            paddingLeft: 2,
                                            paddingRight: 2,
                                            borderRadius: 99,
                                            fontSize: 10.5,
                                            whiteSpace: "nowrap"
                                        }}
                                        label={option.name}
                                        {...getTagProps({ index })}
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} autoFocus variant="outlined" placeholder="Tags" />
                            )}
                            PaperComponent={({ children }) => (
                                <Paper
                                    style={{ width: 400, marginLeft: 6 }}
                                >
                                    {children}
                                </Paper>
                            )}
                        />
                    </div>
                </ClickAwayListener>
            )}
        </div>
    )
}